import type { MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import Caption from '../../Typography/Caption'
import clsx from 'clsx'
import FormatCurrency from 'components/Common/FormatCurrency'
import Clickable from 'components/Common/Clickable'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import styled from 'styled-components'
import { rem } from 'polished'
import Pill from 'LuxKit/Pill/Pill'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import GeoContext from 'contexts/geoContext'

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  originalValue: number
  /**
   * Controls whether the messaging to state the saving is an estimate or exact value.
   * @default estimate
   */
  discountType?: 'exact' | 'estimate'
  /**
   * @example 10 => -10%
   */
  discountPercentage?: number
  size: 'M' | 'L'
  /**
   * @default no-wrap
   */
  wrap?: 'no-wrap' | 'word-wrap'
  /**
   * Won't bubble/propagate.
   */
  onInfoIconClick?: MouseEventHandler<HTMLButtonElement>
}

const InlineClickable = styled(Clickable)`
  display: inline-flex;
`

const CaptionBlock = styled(Caption)`
  display: flex;
  flex-direction: row;
  gap: ${rem(4)};
  align-items: center;

  &.word-wrap {
    flex-wrap: wrap;
  }
`

const ValueAndDiscount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  white-space: nowrap;
  vertical-align: middle;
`

function PriceRowValueDiscountWithCaption(props: Props) {
  const {
    originalValue,
    discountType = 'estimate',
    discountPercentage,
    size,
    wrap = 'no-wrap',
    className,
    onInfoIconClick,
    ...rest
  } = props

  const intl = useIntl()
  const geo = useContext(GeoContext)
  const showStrikethroughPricing = geo.isStrikethroughPricingRegion
  const showModal = useContext(ModalContext)

  const strikethroughExplanation = intl.formatMessage({
    id: 'a5r9irr6gyHVeegY96Zj',
    defaultMessage: 'The discount is calculated by comparing the total value of this offer and the retail price of all inclusions in each package to the offer’s standard rate for the package. The strikethrough price is illustrative of the maximum discount. See Offer Details for full information.',
    description: 'Explanation text for how the strikethrough discount is calculated',
  })
  const discountCalculationTitle = intl.formatMessage({
    id: 'OIqHJOgl2vjmDuv4cQ8c',
    defaultMessage: 'Discount calculation',
    description: 'Modal title for strikethrough discount calculation explanation',
  })
  const discountPercentageLabel = intl.formatMessage(
    {
      id: 'zi0C_2vNbbc3J4pYLKwX',
      defaultMessage: '-{percentage}%',
      description: 'Discount percentage displayed in a pill, e.g. -10%',
    },
    { percentage: discountPercentage ?? 0 },
  )
  const discountPill = !!discountPercentage && <Pill palette="success">
    {discountPercentageLabel}
  </Pill>
  const valuedUpToLabel = intl.formatMessage(
    {
      id: 'RX_DzaFrLcYu3eRH8Lfb',
      defaultMessage: 'Valued up to {price}',
      description: 'Estimated value of the offer showing the original price',
    },
    {
      price: <ValueAndDiscount>
        <FormatCurrency value={originalValue} format="roundedDollar" />
        {discountPill}
      </ValueAndDiscount>,
    },
  )
  const valuedAtLabel = intl.formatMessage(
    {
      id: 'FxZFdgMuxDOAdAD_GQkg',
      defaultMessage: 'Valued at {price}',
      description: 'Exact value of the offer showing the original price',
    },
    {
      price: <ValueAndDiscount>
        <FormatCurrency value={originalValue} format="dynamicDollar" />
        {discountPill}
      </ValueAndDiscount>,
    },
  )

  const handleInfoIconClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    if (showStrikethroughPricing && !onInfoIconClick) {
      showModal(<Modal title={discountCalculationTitle}>
        <BodyText variant="medium">
          {strikethroughExplanation}
        </BodyText>
      </Modal>)
    } else {
      onInfoIconClick?.(event)
    }
  }, [showStrikethroughPricing, onInfoIconClick, showModal, discountCalculationTitle, strikethroughExplanation])

  return <CaptionBlock
    {...rest}
    className={clsx(className, { 'word-wrap': wrap === 'word-wrap' })}
    variant="medium"
    colour="normal-contrast"
    weight="bold"
    wrap={wrap}
  >
    {showStrikethroughPricing && <ValueAndDiscount>
      <s><FormatCurrency value={originalValue} format="roundedDollar" /></s>
      {discountPill}
    </ValueAndDiscount>}

    {!showStrikethroughPricing && <>
      {discountType === 'estimate' && <span>{valuedUpToLabel}</span>}
      {discountType === 'exact' && <span>{valuedAtLabel}</span>}
    </>}

    {(onInfoIconClick || showStrikethroughPricing) && <InlineClickable onClick={handleInfoIconClick}>
      <LineInfoCircleIcon />
    </InlineClickable>}
  </CaptionBlock>
}

export default PriceRowValueDiscountWithCaption
